/**
 * Brand Configuration
 *
 * Centralized branding settings for the storefront.
 * Update these values when customizing for a new store.
 *
 * @example
 * ```tsx
 * import { brandConfig } from "@/config/brand";
 *
 * <title>{brandConfig.siteName}</title>
 * <p>© {new Date().getFullYear()} {brandConfig.copyrightHolder}</p>
 * ```
 */

import { type Locale } from "@/config/locale";

/** Spanish tagline and description, shared with the regional `es-mx`. */
const BRAND_COPY_ES = {
	tagline:
		"Bergdorf Bioscience - Péptidos de grado investigación, calidad farmacéutica. Cada lote verificado independientemente. Pedidos seguros, entrega fiable.",
	description:
		"BergdorfBio - Péptidos de Bergdorf Bioscience. Décadas de experiencia investigadora. Testados, verificados, envío fiable.",
} as const;

export const brandConfig = {
	/** Site name used in titles, metadata, and headers */
	siteName: "BergdorfBio",

	/** Legal entity name for copyright notices */
	copyrightHolder: "Bergdorf Bioscience",

	/** Organization name for structured data (JSON-LD) */
	organizationName: "Bergdorf Bioscience",

	/** Alternate brand spelling, emitted as Organization.alternateName so both name variants map to one entity */
	organizationAlternateName: "BergdorfBio",

	/**
	 * Official, owned profile URLs, emitted as Organization.sameAs for entity disambiguation
	 * (Knowledge Graph / brand SERP). Add only profiles the organization actually controls.
	 */
	sameAs: [
		"https://www.provenexpert.com/de-de/bergdorfbio-hochreine-peptide-forschungsbedarf/",
		"https://de.trustpilot.com/review/bergdorfbio.com",
	],

	/** Primary contact address for legal pages, JSON-LD, deployment defaults */
	supportEmail: "info@bergdorfservice.com",

	/**
	 * Reply-To for all outbound system mail (transactional + marketing).
	 * Dedicated support domain hosted at mailbox.org, polled by Chatwoot -
	 * replies must land in the helpdesk, never in a personal mailbox.
	 */
	replyToEmail: "info@bergdorfservice.com",

	/**
	 * Link domain used in mail and on the durable Vorkasse page: the shop domain.
	 * bergdorfbioscience.com was the link domain while bergdorfbio.com was
	 * Spamhaus-DBL-listed; since 2026-09-29 mail links use the shop domain again,
	 * and old bergdorfbioscience.com links 308 to it at Cloudflare.
	 */
	mailLinkOrigin: "https://bergdorfbio.com",

	/** Default brand name for products without a brand */
	defaultBrand: "Bergdorf Bioscience",

	/** Tagline/description for the store (default/fallback) */
	tagline:
		"Bergdorf Bioscience - Research-grade peptides, pharmaceutical quality. Every batch independently verified. Secure ordering, reliable delivery.",

	/** Homepage meta description (default/fallback) */
	description:
		"BergdorfBio - Peptides from Bergdorf Bioscience. Decades of research expertise. Lab-tested, independently verified, shipped reliably.",

	/** Localized tagline and description */
	localized: {
		en: {
			tagline:
				"Bergdorf Bioscience - Research-grade peptides, pharmaceutical quality. Every batch independently verified. Secure ordering, reliable delivery.",
			description:
				"BergdorfBio - Peptides from Bergdorf Bioscience. Decades of research expertise. Lab-tested, independently verified, shipped reliably.",
		},
		de: {
			tagline:
				"Bergdorf Bioscience - Forschungspeptide in Pharma-Qualität. Jede Charge unabhängig geprüft. Sichere Bestellung, verlässliche Lieferung.",
			description:
				"BergdorfBio - Peptide von Bergdorf Bioscience. Jahrzehntelange Forschungsexpertise. Laborgeprüft, unabhängig verifiziert, zuverlässig geliefert.",
		},
		it: {
			tagline:
				"Bergdorf Bioscience - Peptidi di grado ricerca, qualità farmaceutica. Ogni lotto verificato indipendentemente. Ordini sicuri, consegna affidabile.",
			description:
				"BergdorfBio - Peptidi da Bergdorf Bioscience. Decenni di esperienza nella ricerca. Testati, verificati, spediti in modo affidabile.",
		},
		es: BRAND_COPY_ES,
		"es-mx": BRAND_COPY_ES,
		fr: {
			tagline:
				"Bergdorf Bioscience - Peptides de grade recherche, qualité pharmaceutique. Chaque lot vérifié indépendamment. Commandes sécurisées, livraison fiable.",
			description:
				"BergdorfBio - Peptides de Bergdorf Bioscience. Des décennies d'expertise en recherche. Testés, vérifiés, expédiés de manière fiable.",
		},
		nl: {
			tagline:
				"Bergdorf Bioscience - Onderzoekspeptiden, farmaceutische kwaliteit. Elke batch onafhankelijk geverifieerd. Veilig bestellen, betrouwbare levering.",
			description:
				"BergdorfBio - Peptiden van Bergdorf Bioscience. Decennia aan onderzoeksexpertise. Getest, geverifieerd, betrouwbaar verzonden.",
		},
		pt: {
			tagline:
				"Bergdorf Bioscience - Peptídeos de grau investigação, qualidade farmacêutica. Cada lote verificado independentemente. Encomendas seguras, entrega fiável.",
			description:
				"BergdorfBio - Peptídeos da Bergdorf Bioscience. Décadas de experiência em investigação. Testados, verificados, enviados de forma fiável.",
		},
		"pt-br": {
			tagline:
				"Bergdorf Bioscience - Peptídeos para pesquisa, qualidade farmacêutica. Cada lote verificado de forma independente. Pedidos seguros, entrega confiável.",
			description:
				"BergdorfBio - Peptídeos da Bergdorf Bioscience. Décadas de experiência em pesquisa. Testados, verificados, enviados com confiabilidade.",
		},
		pl: {
			tagline:
				"Bergdorf Bioscience - Peptydy badawcze, jakość farmaceutyczna. Każda partia niezależnie zweryfikowana. Bezpieczne zamawianie, niezawodna dostawa.",
			description:
				"BergdorfBio - Peptydy od Bergdorf Bioscience. Dekady doświadczenia badawczego. Przetestowane, zweryfikowane, niezawodnie wysyłane.",
		},
		sv: {
			tagline:
				"Bergdorf Bioscience - Forskningspeptider, farmaceutisk kvalitet. Varje batch oberoende verifierad. Säker beställning, pålitlig leverans.",
			description:
				"BergdorfBio - Peptider från Bergdorf Bioscience. Decennier av forskningserfarenhet. Testade, verifierade, levererade på ett pålitligt sätt.",
		},
		da: {
			tagline:
				"Bergdorf Bioscience - Forskningspeptider, farmaceutisk kvalitet. Hver batch uafhængigt verificeret. Sikker bestilling, pålidelig levering.",
			description:
				"BergdorfBio - Peptider fra Bergdorf Bioscience. Årtiers forskningsekspertise. Testet, verificeret, leveret pålideligt.",
		},
		el: {
			tagline:
				"Bergdorf Bioscience - Ερευνητικά πεπτίδια, φαρμακευτική ποιότητα. Κάθε παρτίδα επαληθευμένη ανεξάρτητα. Ασφαλείς παραγγελίες, αξιόπιστη παράδοση.",
			description:
				"BergdorfBio - Πεπτίδια από τη Bergdorf Bioscience. Δεκαετίες ερευνητικής εμπειρίας. Δοκιμασμένα, επαληθευμένα, αξιόπιστη αποστολή.",
		},
		cs: {
			tagline:
				"Bergdorf Bioscience - Výzkumné peptidy farmaceutické kvality. Každá šarže nezávisle ověřena. Bezpečné objednávání, spolehlivé dodání.",
			description:
				"BergdorfBio - Peptidy od Bergdorf Bioscience. Desetiletí výzkumných zkušeností. Laboratorně testovány, nezávisle ověřeny, spolehlivě dodávány.",
		},
		fi: {
			tagline:
				"Bergdorf Bioscience - Tutkimuslaatua olevat peptidit, farmaseuttinen laatu. Jokainen erä itsenäisesti varmennettu. Turvallinen tilaaminen, luotettava toimitus.",
			description:
				"BergdorfBio - Peptidit Bergdorf Biosciencelta. Vuosikymmenten tutkimuskokemus. Laboratoriotestattu, itsenäisesti varmennettu, luotettavasti toimitettu.",
		},
		ro: {
			tagline:
				"Bergdorf Bioscience - Peptide de grad cercetare, calitate farmaceutică. Fiecare lot verificat independent. Comenzi sigure, livrare fiabilă.",
			description:
				"BergdorfBio - Peptide de la Bergdorf Bioscience. Decenii de expertiză în cercetare. Testate, verificate independent, livrate cu fiabilitate.",
		},
		hu: {
			tagline:
				"Bergdorf Bioscience - Kutatási szintű peptidek, gyógyszerészeti minőség. Minden tétel függetlenül ellenőrizve. Biztonságos rendelés, megbízható szállítás.",
			description:
				"BergdorfBio - Peptidek a Bergdorf Bioscience-től. Évtizedes kutatási tapasztalat. Laboratóriumban tesztelve, függetlenül ellenőrizve, megbízhatóan szállítva.",
		},
		sk: {
			tagline:
				"Bergdorf Bioscience - Výskumné peptidy farmaceutickej kvality. Každá šarža nezávisle overená. Bezpečné objednávanie, spoľahlivé dodanie.",
			description:
				"BergdorfBio - Peptidy od Bergdorf Bioscience. Desaťročia výskumných skúseností. Laboratórne testované, nezávisle overené, spoľahlivo dodávané.",
		},
		tr: {
			tagline:
				"Bergdorf Bioscience - Farmasötik kalitede araştırma peptitleri. Her parti bağımsız olarak doğrulanmıştır. Güvenli sipariş, güvenilir teslimat.",
			description:
				"BergdorfBio - Bergdorf Bioscience'dan peptitler. On yıllara dayanan araştırma uzmanlığı. Laboratuvarda test edilmiş, bağımsız olarak doğrulanmış, güvenilir şekilde teslim edilir.",
		},
		nb: {
			tagline:
				"Bergdorf Bioscience - Forskningspeptider av farmasøytisk kvalitet. Hver batch uavhengig verifisert. Sikker bestilling, pålitelig levering.",
			description:
				"BergdorfBio - Peptider fra Bergdorf Bioscience. Tiår med forskningskompetanse. Laboratorietestet, uavhengig verifisert, levert pålitelig.",
		},
		bg: {
			tagline:
				"Bergdorf Bioscience - Изследователски пептиди, фармацевтично качество. Всяка партида независимо верифицирана. Сигурно поръчване, надеждна доставка.",
			description:
				"BergdorfBio - Пептиди от Bergdorf Bioscience. Десетилетия изследователски опит. Лабораторно тествани, независимо верифицирани, надеждно доставяни.",
		},
		hr: {
			tagline:
				"Bergdorf Bioscience - Istraživački peptidi, farmaceutska kvaliteta. Svaka serija neovisno verificirana. Sigurno naručivanje, pouzdana dostava.",
			description:
				"BergdorfBio - Peptidi od Bergdorf Bioscience. Desetljeća istraživačkog iskustva. Laboratorijski testirani, neovisno verificirani, pouzdano isporučeni.",
		},
		sl: {
			tagline:
				"Bergdorf Bioscience - Raziskovalni peptidi farmacevtske kakovosti. Vsaka serija neodvisno preverjena. Varno naročilo, zanesljiva dostava.",
			description:
				"BergdorfBio - Peptidi podjetja Bergdorf Bioscience. Desetletja raziskovalnih izkušenj. Preverjeni v laboratoriju, neodvisno potrjeni, zanesljivo dostavljeni.",
		},
		sr: {
			tagline:
				"Bergdorf Bioscience - Istraživački peptidi farmaceutske kvaliteta. Svaka serija nezavisno proverena. Sigurno naručivanje, pouzdana isporuka.",
			description:
				"BergdorfBio - Peptidi kompanije Bergdorf Bioscience. Decenije istraživačkog iskustva. Laboratorijski testirani, nezavisno verifikovani, pouzdano isporučeni.",
		},
		bs: {
			tagline:
				"Bergdorf Bioscience - Istraživački peptidi farmaceutske kvaliteta. Svaka serija nezavisno provjerena. Sigurno naručivanje, pouzdana isporuka.",
			description:
				"BergdorfBio - Peptidi kompanije Bergdorf Bioscience. Decenije istraživačkog iskustva. Laboratorijski testirani, nezavisno provjereni, pouzdano isporučeni.",
		},
		mk: {
			tagline:
				"Bergdorf Bioscience - Истражувачки пептиди со фармацевтски квалитет. Секоја серија е независно проверена. Безбедно нарачување, сигурна испорака.",
			description:
				"BergdorfBio - Пептиди од Bergdorf Bioscience. Долгогодишно искуство во истражување. Тестирани во лабораторија, независно верифицирани, сигурно испорачани.",
		},
		lt: {
			tagline:
				"Bergdorf Bioscience - Moksliniai peptidai, farmacinė kokybė. Kiekviena partija nepriklausomai patikrinta. Saugus užsakymas, patikimas pristatymas.",
			description:
				"BergdorfBio - Bergdorf Bioscience peptidai. Dešimtmečiai mokslinės patirties. Ištirti laboratorijoje, nepriklausomai patvirtinti, patikimai pristatyti.",
		},
		lv: {
			tagline:
				"Bergdorf Bioscience - Pētniecības peptīdi, farmaceitiskā kvalitāte. Katra partija neatkarīgi pārbaudīta. Droša pasūtīšana, uzticama piegāde.",
			description:
				"BergdorfBio - Bergdorf Bioscience peptīdi. Gadu desmiti pētniecības pieredzes. Pārbaudīti laboratorijā, neatkarīgi apstiprināti, uzticami piegādāti.",
		},
		et: {
			tagline:
				"Bergdorf Bioscience - Teaduslikud peptiidid, farmatseutiline kvaliteet. Iga partii sõltumatult kontrollitud. Turvaline tellimine, usaldusväärne kohaletoimetamine.",
			description:
				"BergdorfBio - Bergdorf Bioscience peptiidid. Aastakümned teaduskogemust. Laboris testitud, sõltumatult kinnitatud, usaldusväärselt tarnitud.",
		},
	} satisfies Record<Locale, { tagline: string; description: string }>,

	/** Logo aria-label for accessibility */
	logoAriaLabel: "Bergdorf Bioscience",

	/** Title template - %s will be replaced with page title */
	titleTemplate: "%s | BergdorfBio",

	/** Social media handles */
	social: {
		/** Twitter/X handle (without @) - set to null to disable */
		twitter: null as string | null,
		/** Instagram handle (without @) - set to null to disable */
		instagram: null as string | null,
		/** Facebook page URL - set to null to disable */
		facebook: null as string | null,
	},
} as const;

/**
 * Helper to format page title using brand template.
 */
export function formatPageTitle(title: string): string {
	return brandConfig.titleTemplate.replace("%s", title);
}

/** Bestseller product slugs for upsell recommendations in cart drawer */
export const UPSELL_BESTSELLERS = ["ghk-cu-50mg", "retatrutide-5mg", "glow-stack"] as const;

/**
 * Products shown as essential accessories on matching category PDPs and in cart drawer.
 * categoryTriggers must match Saleor category names exactly (case-sensitive).
 */
export const ESSENTIAL_ACCESSORIES = [{ slug: "bac-water-10ml", categoryTriggers: ["Peptides"] }] as const;
